import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AuditEventSummary,
  NameI18n,
  ResourceState,
  ResourceVersionSummary,
} from '@displaycms/contracts';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Icon, type IconName } from '@/components/icon';
import { pickName } from '@/lib/i18n-name';
import { STATE_BADGE_VARIANT } from '@/lib/state-badge';
import { Alert } from '@/components/ui/alert';
import { MediaPreview } from '@/components/media/MediaPreview';
import { useAuth } from '@/auth/auth-context';
import { ApiError } from '@/api/client';
import {
  approve as apiApprove,
  getResource,
  listResourceAuditEvents,
  listResourceVersions,
  recommendApprove as apiRecommendApprove,
  reattest as apiReattest,
  reject as apiReject,
  revoke as apiRevoke,
  submitForReview as apiSubmitForReview,
  updateResource,
  type ApproveOptions,
  type AuditEventsResponse,
  type ResourceDetail,
  type ResourceVersionsResponse,
} from '@/api/resources';
import {
  getManifest,
  listPublicKeys,
  verifyManifestClient,
  type ManifestEnvelope,
} from '@/api/manifests';
import type { PublicKeyEntry } from '@displaycms/contracts';
import {
  formatBytes as formatBytesShared,
  formatDate as formatDateShared,
  type Locale as LocaleType,
} from '@/lib/format';

/**
 * `/resources/:id` — resource detail page.
 *
 * Layout: header (name + kind + state) → versions table → audit timeline
 * → signed-manifest viewer (with a client-side verify badge) → action
 * drawer with role/state-gated buttons.
 *
 * The action drawer never tries to predict server-side authorisation
 * beyond the obvious permission name; the server is authoritative for
 * the dual-approval ("must differ from recommender") check and surfaces
 * a 403 if violated. Dialogs collect the rationale / TTL inputs the
 * audit endpoints require.
 */

// Default approve/reattest TTL — per task brief: 6 months. The server
// caps at 24 months (`63072000` seconds, see `AuditActionInput`).
const SIX_MONTHS_SECONDS = 60 * 60 * 24 * 30 * 6;

type Lang = 'fr' | 'en';

// Inactive Radix tab panels stay mounted (forceMount) but display:none — the
// 2026 detail-page pattern. This keeps every panel (versions, timeline,
// manifest) in the DOM so the unit tests query their testids across panels
// without clicking a tab, while only the active tab is visible.
const TAB_PANEL = 'space-y-4 data-[state=inactive]:hidden';

export function ResourceDetailPage(): JSX.Element {
  const { t, i18n: i18nInstance } = useTranslation(['audit', 'resources', 'common']);
  const lang: Lang = i18nInstance.language === 'en' ? 'en' : 'fr';
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';

  // ---- Queries ------------------------------------------------------------
  const resourceQuery = useQuery<ResourceDetail>({
    queryKey: ['resource', 'detail', id],
    queryFn: () => getResource(id),
    enabled: id.length > 0,
  });

  const versionsQuery = useQuery<ResourceVersionsResponse>({
    queryKey: ['resource', 'versions', id],
    queryFn: () => listResourceVersions(id),
    enabled: id.length > 0,
  });

  const auditQuery = useQuery<AuditEventsResponse>({
    queryKey: ['resource', 'audit-events', id],
    queryFn: () => listResourceAuditEvents(id),
    enabled: id.length > 0,
  });

  // All hooks must run unconditionally on every render. Compute the
  // memoised version list here, BEFORE any early-return branch, so the
  // hook order is stable across the loading → loaded transition.
  const resourceData = resourceQuery.data ?? null;
  const versions = versionsQuery.data?.items ?? [];
  const auditEvents = auditQuery.data?.items ?? [];
  const versionsSorted: ResourceVersionSummary[] = useMemo(() => {
    if (versions.length > 0) {
      return [...versions].sort((a, b) => b.version_number - a.version_number);
    }
    return resourceData?.current_version ? [resourceData.current_version] : [];
  }, [versions, resourceData]);

  if (id.length === 0 || resourceQuery.isError) {
    return (
      <Alert variant="danger" data-testid="detail-error">
        {t('audit:not_found')}
      </Alert>
    );
  }

  if (resourceQuery.isLoading || !resourceData) {
    return (
      <p role="status" data-testid="detail-loading" className="text-sm text-muted-foreground">
        {t('audit:loading')}
      </p>
    );
  }

  const resource = resourceData;
  const currentVersion = resource.current_version ?? null;

  return (
    <div className="flex flex-col gap-6" data-testid="resource-detail-page">
      {/* ---------------------------------------------------------------- *
       * HERO — kind icon + name + identifier, state/kind cluster, and the
       * primary action row (rename).
       * ---------------------------------------------------------------- */}
      <Header resource={resource} lang={lang} />

      {/* ---------------------------------------------------------------- *
       * BODY — tabbed main column + sticky "Résumé" side rail.
       * ---------------------------------------------------------------- */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <main className="lg:col-span-2">
          <Tabs defaultValue="preview" className="w-full">
            <TabsList className="flex h-auto flex-wrap justify-start gap-1">
              <TabsTrigger value="preview" data-testid="tab-preview" className="gap-1.5">
                <Icon name={kindIcon(resource.kind)} size={14} aria-hidden />
                {t('resources:detail.tab_preview')}
              </TabsTrigger>
              <TabsTrigger value="metadata" data-testid="tab-metadata" className="gap-1.5">
                <Icon name="layers" size={14} aria-hidden />
                {t('resources:detail.tab_metadata')}
              </TabsTrigger>
              <TabsTrigger value="validation" data-testid="tab-validation" className="gap-1.5">
                <Icon name="check-circle" size={14} aria-hidden />
                {t('resources:detail.tab_validation')}
              </TabsTrigger>
            </TabsList>

            {/* --- Aperçu: media preview (default/active tab) --- */}
            <TabsContent value="preview" forceMount className={TAB_PANEL}>
              <Card aria-labelledby="preview-heading">
                <SectionTitle id="preview-heading" icon={kindIcon(resource.kind)}>
                  {t('resources:detail.tab_preview')}
                </SectionTitle>
                <CardContent>
                  {/* Preview — visible for any state so an auditeur sees the
                      actual content before recommend/approve/reject. */}
                  {resource.current_version_id ? (
                    <section
                      data-testid="resource-preview"
                      aria-label={t('resources:preview.alt')}
                      className="flex justify-center rounded-md border border-border bg-muted/30 p-3"
                    >
                      <MediaPreview
                        source="resource"
                        resourceVersionId={resource.current_version_id}
                        kind={resource.kind}
                        size="full"
                      />
                    </section>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('resources:preview.unavailable')}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* --- Métadonnées: versions table --- */}
            <TabsContent value="metadata" forceMount className={TAB_PANEL}>
              <Card aria-labelledby="versions-heading">
                <SectionTitle id="versions-heading" icon="layers">
                  {t('audit:sections.versions')}
                </SectionTitle>
                <CardContent>
                  <VersionsTable
                    versions={versionsSorted}
                    currentVersionId={resource.current_version_id}
                    lang={lang}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* --- Validation: audit timeline + signed manifest --- */}
            <TabsContent value="validation" forceMount className={TAB_PANEL}>
              <Card aria-labelledby="timeline-heading">
                <SectionTitle id="timeline-heading" icon="clock">
                  {t('audit:sections.timeline')}
                </SectionTitle>
                <CardContent>
                  <AuditTimeline
                    events={auditEvents}
                    isLoading={auditQuery.isLoading}
                    isError={auditQuery.isError}
                    lang={lang}
                  />
                </CardContent>
              </Card>

              <Card aria-labelledby="manifest-heading">
                <SectionTitle id="manifest-heading" icon="check-circle">
                  {t('audit:sections.manifest')}
                </SectionTitle>
                <CardContent>
                  <ManifestViewer
                    manifestId={resource.current_version?.signed_manifest_id ?? null}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>

        {/* --- Side rail: "Résumé" summary + approval actions --- */}
        <aside className="lg:col-span-1">
          <Card className="lg:sticky lg:top-6">
            <SectionTitle id="summary-heading" icon="info">
              {t('resources:detail.summary_heading')}
            </SectionTitle>
            <CardContent className="flex flex-col gap-4">
              {/* State + kind pills */}
              <div className="flex flex-wrap items-center gap-2">
                <StateBadge state={resource.state} />
                <Badge variant="neutral" icon={kindIcon(resource.kind)}>
                  {t(`resources:kind.${resource.kind}`)}
                </Badge>
              </div>

              <dl className="flex flex-col gap-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <dt className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon name={kindIcon(resource.kind)} size={14} aria-hidden />
                    {t('audit:header.kind_label')}
                  </dt>
                  <dd className="text-right">{t(`resources:kind.${resource.kind}`)}</dd>
                </div>
                {currentVersion && currentVersion.size_bytes !== null && (
                  <div className="flex items-center justify-between gap-2">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <Icon name="layers" size={14} aria-hidden />
                      {t('audit:versions.column.size')}
                    </dt>
                    <dd className="text-right">{formatBytes(currentVersion.size_bytes, lang)}</dd>
                  </div>
                )}
                {currentVersion && (
                  <div className="flex items-center justify-between gap-2">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <Icon name="info" size={14} aria-hidden />
                      {t('audit:versions.column.version')}
                    </dt>
                    <dd className="text-right">#{currentVersion.version_number}</dd>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <dt className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon name="calendar" size={14} aria-hidden />
                    {t('audit:versions.column.created_at')}
                  </dt>
                  <dd className="text-right">{formatDate(resource.created_at, lang)}</dd>
                </div>
              </dl>

              {/* Approval / 4-eyes actions */}
              <div className="flex flex-col gap-2 border-t border-border pt-4">
                <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Icon name="check-circle" size={16} aria-hidden />
                  {t('audit:sections.actions')}
                </h3>
                <ActionDrawer resource={resource} />
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header({ resource, lang }: { resource: ResourceDetail; lang: Lang }): JSX.Element {
  const { t } = useTranslation(['audit', 'resources']);
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const displayName = pickName(resource.name_i18n, lang);

  const [renameMode, setRenameMode] = useState(false);
  const [frDraft, setFrDraft] = useState('');
  const [enDraft, setEnDraft] = useState('');
  const renameMutation = useMutation<unknown, ApiError, { fr: string; en?: string }>({
    mutationFn: async (next) =>
      updateResource(resource.id, {
        name_i18n: next.en ? { fr: next.fr, en: next.en } : { fr: next.fr },
      }),
    onSuccess: () => {
      setRenameMode(false);
      // Detail page uses queryKey ['resource', 'detail', id] (singular);
      // list pages use ['resources', 'list']. Invalidate both.
      void queryClient.invalidateQueries({
        queryKey: ['resource', 'detail', resource.id],
      });
      void queryClient.invalidateQueries({ queryKey: ['resources', 'list'] });
    },
  });

  return (
    <header className="flex flex-col gap-4" data-testid="detail-header">
      <Link
        to="/resources"
        className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:underline"
        data-testid="back-to-list"
      >
        <Icon name="chevron-left" size={16} aria-hidden />
        {t('audit:back_to_list')}
      </Link>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
            <Icon name={kindIcon(resource.kind)} size={28} aria-hidden />
          </span>
          <div className="flex flex-col gap-1">
            {renameMode ? (
              <div className="flex flex-wrap items-center gap-2" data-testid="resource-rename-form">
                <Input
                  value={frDraft}
                  onChange={(e) => setFrDraft(e.currentTarget.value)}
                  placeholder="Nom FR"
                  data-testid="resource-rename-fr"
                  className="w-48"
                />
                <Input
                  value={enDraft}
                  onChange={(e) => setEnDraft(e.currentTarget.value)}
                  placeholder="Nom EN (optionnel)"
                  data-testid="resource-rename-en"
                  className="w-48"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    if (frDraft.trim().length === 0) return;
                    renameMutation.mutate(
                      enDraft.trim().length > 0
                        ? { fr: frDraft.trim(), en: enDraft.trim() }
                        : { fr: frDraft.trim() },
                    );
                  }}
                  disabled={renameMutation.isPending || frDraft.trim().length === 0}
                  data-testid="resource-rename-save"
                >
                  {t('common:save', { defaultValue: 'Enregistrer' })}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setRenameMode(false)}
                  disabled={renameMutation.isPending}
                  data-testid="resource-rename-cancel"
                >
                  {t('common:cancel', { defaultValue: 'Annuler' })}
                </Button>
              </div>
            ) : (
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="detail-name">
                {displayName}
              </h1>
            )}
            <code className="font-mono text-xs text-muted-foreground">{resource.id}</code>
            <span className="text-sm text-muted-foreground" data-testid="detail-kind">
              {t('audit:header.kind_label')}: {t(`resources:kind.${resource.kind}`)}
            </span>
          </div>
        </div>

        {/* State + kind cluster (with a small created-at meta) */}
        <div className="flex flex-col items-start gap-2 lg:items-end">
          <div className="flex flex-wrap items-center gap-2">
            <StateBadge state={resource.state} />
            <Badge variant="neutral" icon={kindIcon(resource.kind)}>
              {t(`resources:kind.${resource.kind}`)}
            </Badge>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="calendar" size={14} aria-hidden />
            {t('audit:versions.column.created_at')}: {formatDate(resource.created_at, lang)}
          </span>
        </div>
      </div>

      {/* Primary actions */}
      {hasPermission('resources.update') && !renameMode && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              setFrDraft(resource.name_i18n.fr);
              setEnDraft(resource.name_i18n.en ?? '');
              setRenameMode(true);
            }}
            data-testid="resource-rename-btn"
          >
            <Icon name="settings" size={16} aria-hidden />
            {t('common:rename', { defaultValue: 'Renommer' })}
          </Button>
        </div>
      )}
    </header>
  );
}

function StateBadge({ state }: { state: ResourceState }): JSX.Element {
  const { t } = useTranslation(['audit']);
  return (
    <Badge variant={STATE_BADGE_VARIANT[state] ?? 'neutral'} data-testid={`detail-state-badge-${state}`}>
      {t(`audit:state.${state}`)}
    </Badge>
  );
}


// ---------------------------------------------------------------------------
// Versions table
// ---------------------------------------------------------------------------

interface VersionsTableProps {
  versions: ReadonlyArray<ResourceVersionSummary>;
  currentVersionId: string | null;
  lang: Lang;
}

function VersionsTable({ versions, currentVersionId, lang }: VersionsTableProps): JSX.Element {
  const { t } = useTranslation(['audit']);
  if (versions.length === 0) {
    return (
      <p data-testid="versions-empty" className="text-sm text-muted-foreground">
        {t('audit:versions.empty')}
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm" data-testid="versions-table">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-3 py-2 text-left font-medium">{t('audit:versions.column.version')}</th>
            <th className="px-3 py-2 text-left font-medium">{t('audit:versions.column.payload_kind')}</th>
            <th className="px-3 py-2 text-left font-medium">{t('audit:versions.column.size')}</th>
            <th className="px-3 py-2 text-left font-medium">{t('audit:versions.column.sha256')}</th>
            <th className="px-3 py-2 text-left font-medium">{t('audit:versions.column.state')}</th>
            <th className="px-3 py-2 text-left font-medium">{t('audit:versions.column.created_at')}</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.id} className="border-t border-border" data-testid={`version-row-${v.id}`}>
              <td className="px-3 py-2 font-medium">
                #{v.version_number}
                {v.id === currentVersionId && (
                  <span
                    className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-[hsl(var(--primary-text))]"
                    data-testid={`version-current-${v.id}`}
                  >
                    {t('audit:versions.current_badge')}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {t(`audit:versions.payload_kind.${v.payload_kind}`)}
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {v.size_bytes !== null ? formatBytes(v.size_bytes, lang) : t('audit:versions.size_unknown')}
              </td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground" data-testid={`version-sha-${v.id}`}>
                {v.bytes_sha256
                  ? t('audit:versions.sha_short', { prefix: v.bytes_sha256.slice(0, 8) })
                  : t('audit:versions.size_unknown')}
              </td>
              <td className="px-3 py-2">
                <StateBadge state={v.state} />
              </td>
              <td className="px-3 py-2 text-muted-foreground">{formatDate(v.created_at, lang)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit timeline
// ---------------------------------------------------------------------------

interface TimelineProps {
  events: ReadonlyArray<AuditEventSummary>;
  isLoading: boolean;
  isError: boolean;
  lang: Lang;
}

function AuditTimeline({ events, isLoading, isError, lang }: TimelineProps): JSX.Element {
  const { t } = useTranslation(['audit']);
  if (isLoading) {
    return (
      <p role="status" data-testid="timeline-loading" className="text-sm text-muted-foreground">
        {t('audit:loading')}
      </p>
    );
  }
  if (isError) {
    return (
      <p role="alert" data-testid="timeline-error" className="text-sm text-destructive">
        {t('audit:timeline.load_error')}
      </p>
    );
  }
  if (events.length === 0) {
    return (
      <p data-testid="timeline-empty" className="text-sm text-muted-foreground">
        {t('audit:timeline.empty')}
      </p>
    );
  }
  const sorted = [...events].sort(
    (a, b) => Date.parse(a.created_at) - Date.parse(b.created_at),
  );
  return (
    <ol className="flex flex-col gap-2" data-testid="audit-timeline">
      {sorted.map((ev) => (
        <li
          key={ev.id}
          className="rounded-md border border-border bg-card p-3"
          data-testid={`audit-event-${ev.id}`}
        >
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="font-medium" data-testid={`audit-event-action-${ev.id}`}>
              {t(`audit:action_name.${ev.action}`)}
            </span>
            <span className="text-xs text-muted-foreground">{formatDateTime(ev.created_at, lang)}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('audit:timeline.by', { actor: ev.actor_username ?? ev.actor_id })}
          </p>
          {ev.rationale && (
            <p className="mt-1 text-sm" data-testid={`audit-event-rationale-${ev.id}`}>
              <span className="font-medium">{t('audit:timeline.rationale_label')}:</span> {ev.rationale}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Signed manifest viewer
// ---------------------------------------------------------------------------

interface ManifestViewerProps {
  manifestId: string | null;
}

function ManifestViewer({ manifestId }: ManifestViewerProps): JSX.Element {
  const { t } = useTranslation(['audit']);
  const [expanded, setExpanded] = useState<boolean>(false);

  const manifestQuery = useQuery<ManifestEnvelope>({
    queryKey: ['manifest', manifestId],
    queryFn: () => getManifest(manifestId as string),
    enabled: manifestId !== null,
  });

  const keysQuery = useQuery<PublicKeyEntry[]>({
    queryKey: ['signing-keys'],
    queryFn: () => listPublicKeys(),
    enabled: manifestId !== null,
  });

  const publicKeyB64 = useMemo(() => {
    if (!manifestQuery.data || !keysQuery.data) return null;
    // The server returns a plain PublicKeyEntry[] (not { items: [...] }).
    const entry = keysQuery.data.find(
      (k) => k.kid === manifestQuery.data!.signing_key_id,
    );
    return entry?.public_key_b64 ?? null;
  }, [manifestQuery.data, keysQuery.data]);

  const [verifyStatus, setVerifyStatus] = useState<'idle' | 'verifying' | 'ok' | 'invalid'>('idle');

  useEffect(() => {
    let alive = true;
    if (!manifestQuery.data || !publicKeyB64) {
      setVerifyStatus('idle');
      return () => {
        alive = false;
      };
    }
    setVerifyStatus('verifying');
    void verifyManifestClient({
      canonicalJson: manifestQuery.data.canonical_json,
      signatureB64: manifestQuery.data.signature_b64,
      publicKeyB64,
    }).then((ok) => {
      if (!alive) return;
      setVerifyStatus(ok ? 'ok' : 'invalid');
    });
    return () => {
      alive = false;
    };
  }, [manifestQuery.data, publicKeyB64]);

  if (manifestId === null) {
    return (
      <p data-testid="manifest-none" className="text-sm text-muted-foreground">
        {t('audit:manifest.none')}
      </p>
    );
  }
  if (manifestQuery.isError || keysQuery.isError) {
    return (
      <p role="alert" data-testid="manifest-load-error" className="text-sm text-destructive">
        {t('audit:manifest.load_error')}
      </p>
    );
  }
  if (manifestQuery.isLoading || keysQuery.isLoading || !manifestQuery.data) {
    return (
      <p role="status" data-testid="manifest-loading" className="text-sm text-muted-foreground">
        {t('audit:loading')}
      </p>
    );
  }

  const envelope = manifestQuery.data;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border bg-card p-3" data-testid="manifest-viewer">
      <div className="flex items-center gap-3">
        <VerifyBadge status={verifyStatus} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          data-testid="manifest-toggle"
        >
          {expanded ? t('audit:manifest.toggle_hide') : t('audit:manifest.toggle_show')}
        </Button>
      </div>
      {expanded && (
        <dl className="grid grid-cols-1 gap-2 text-xs">
          <div>
            <dt className="font-medium">{t('audit:manifest.key_id_label')}</dt>
            <dd className="font-mono text-muted-foreground" data-testid="manifest-kid">
              {envelope.signing_key_id}
            </dd>
          </div>
          <div>
            <dt className="font-medium">{t('audit:manifest.signature_label')}</dt>
            <dd className="break-all font-mono text-muted-foreground" data-testid="manifest-signature">
              {envelope.signature_b64}
            </dd>
          </div>
          <div>
            <dt className="font-medium">{t('audit:manifest.canonical_label')}</dt>
            <dd>
              <pre
                className="overflow-x-auto rounded bg-muted/40 p-2 font-mono text-xs"
                data-testid="manifest-canonical"
              >
                {envelope.canonical_json}
              </pre>
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function VerifyBadge({ status }: { status: 'idle' | 'verifying' | 'ok' | 'invalid' }): JSX.Element {
  const { t } = useTranslation(['audit']);
  if (status === 'verifying' || status === 'idle') {
    return (
      <span
        className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
        data-testid="manifest-verify-status"
        data-status="verifying"
      >
        {t('audit:manifest.verifying')}
      </span>
    );
  }
  if (status === 'ok') {
    return (
      <span
        className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-900"
        data-testid="manifest-verify-status"
        data-status="ok"
      >
        {t('audit:manifest.verified')}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-900"
      data-testid="manifest-verify-status"
      data-status="invalid"
    >
      {t('audit:manifest.invalid')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Action drawer
// ---------------------------------------------------------------------------

type DialogKind = null | 'reject' | 'revoke' | 'approve' | 'reattest';

function ActionDrawer({ resource }: { resource: ResourceDetail }): JSX.Element {
  const { t } = useTranslation(['audit']);
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; key: string } | null>(null);

  const invalidate = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: ['resource', 'detail', resource.id] });
    void queryClient.invalidateQueries({ queryKey: ['resource', 'audit-events', resource.id] });
    void queryClient.invalidateQueries({ queryKey: ['resource', 'versions', resource.id] });
    void queryClient.invalidateQueries({ queryKey: ['resources', 'list'] });
  }, [queryClient, resource.id]);

  const handleSuccess = useCallback((): void => {
    setFeedback({ kind: 'ok', key: 'audit:actions.success' });
    setDialog(null);
    invalidate();
  }, [invalidate]);

  const handleError = useCallback((_err: unknown): void => {
    setFeedback({ kind: 'err', key: 'audit:actions.error' });
  }, []);

  // Mutations — one per audit action.
  const submitMut = useMutation({
    mutationFn: () => apiSubmitForReview(resource.id),
    onSuccess: handleSuccess,
    onError: handleError,
  });
  const recommendMut = useMutation({
    mutationFn: () => apiRecommendApprove(resource.id),
    onSuccess: handleSuccess,
    onError: handleError,
  });
  const approveMut = useMutation({
    mutationFn: (opts: ApproveOptions) => apiApprove(resource.id, opts),
    onSuccess: handleSuccess,
    onError: handleError,
  });
  const rejectMut = useMutation({
    mutationFn: (rationale: string) => apiReject(resource.id, rationale),
    onSuccess: handleSuccess,
    onError: handleError,
  });
  const revokeMut = useMutation({
    mutationFn: (rationale: string) => apiRevoke(resource.id, rationale),
    onSuccess: handleSuccess,
    onError: handleError,
  });
  const reattestMut = useMutation({
    mutationFn: (opts: ApproveOptions) => apiReattest(resource.id, opts),
    onSuccess: handleSuccess,
    onError: handleError,
  });

  // ---- gating: by state × permission ---------------------------------------
  const canSubmit = hasPermission('resources.submit');
  const canRecommend = hasPermission('resources.recommend');
  const canApprove = hasPermission('resources.approve');
  const canReject = hasPermission('resources.reject');
  const canRevoke = hasPermission('resources.revoke');
  const canReattest = hasPermission('resources.reattest');

  const state = resource.state;

  // Build the list of visible buttons per state machine in design §3.
  const buttons: ReactButtonSpec[] = [];
  if ((state === 'draft' || state === 'revoked' || state === 'expired') && canSubmit) {
    buttons.push({
      key: 'submit_for_review',
      label: t('audit:actions.submit_for_review'),
      icon: 'chevron-up',
      onClick: () => submitMut.mutate(),
      pending: submitMut.isPending,
      variant: 'default',
    });
  }
  if (state === 'pending_review' && canRecommend) {
    buttons.push({
      key: 'recommend_approve',
      label: t('audit:actions.recommend_approve'),
      icon: 'check',
      onClick: () => recommendMut.mutate(),
      pending: recommendMut.isPending,
      variant: 'default',
    });
  }
  if (state === 'recommended' && canApprove) {
    buttons.push({
      key: 'approve',
      label: t('audit:actions.approve'),
      icon: 'check-circle',
      onClick: () => setDialog('approve'),
      pending: approveMut.isPending,
      variant: 'default',
    });
  }
  if ((state === 'pending_review' || state === 'recommended') && canReject) {
    buttons.push({
      key: 'reject',
      label: t('audit:actions.reject'),
      icon: 'x-circle',
      onClick: () => setDialog('reject'),
      pending: rejectMut.isPending,
      variant: 'outline',
    });
  }
  if (state === 'approved' && canRevoke) {
    buttons.push({
      key: 'revoke',
      label: t('audit:actions.revoke'),
      icon: 'trash-2',
      onClick: () => setDialog('revoke'),
      pending: revokeMut.isPending,
      variant: 'outline',
    });
  }
  if (state === 'approved' && canReattest) {
    buttons.push({
      key: 'reattest',
      label: t('audit:actions.reattest'),
      icon: 'refresh-cw',
      onClick: () => setDialog('reattest'),
      pending: reattestMut.isPending,
      variant: 'default',
    });
  }

  return (
    <div className="flex flex-col gap-2" data-testid="action-drawer">
      <div className="flex flex-wrap items-center gap-2">
        {buttons.length === 0 ? (
          <p data-testid="action-drawer-empty" className="text-sm text-muted-foreground">
            {t('audit:actions.no_actions')}
          </p>
        ) : (
          buttons.map((b) => (
            <Button
              key={b.key}
              type="button"
              variant={b.variant}
              className="gap-2"
              onClick={b.onClick}
              disabled={b.pending}
              data-testid={`action-${b.key}`}
            >
              <Icon name={b.icon} size={16} aria-hidden />
              {b.label}
            </Button>
          ))
        )}
      </div>

      {feedback && (
        <p
          role={feedback.kind === 'err' ? 'alert' : 'status'}
          data-testid={`action-feedback-${feedback.kind}`}
          className={
            feedback.kind === 'err'
              ? 'text-sm text-destructive'
              : 'text-sm text-green-700'
          }
        >
          {t(feedback.key)}
        </p>
      )}

      {dialog === 'reject' && (
        <RationaleDialog
          titleKey="audit:dialog.reject_title"
          onCancel={() => setDialog(null)}
          onConfirm={(rationale) => rejectMut.mutate(rationale)}
          pending={rejectMut.isPending}
        />
      )}
      {dialog === 'revoke' && (
        <RationaleDialog
          titleKey="audit:dialog.revoke_title"
          onCancel={() => setDialog(null)}
          onConfirm={(rationale) => revokeMut.mutate(rationale)}
          pending={revokeMut.isPending}
        />
      )}
      {dialog === 'approve' && (
        <ApproveDialog
          titleKey="audit:dialog.approve_title"
          onCancel={() => setDialog(null)}
          onConfirm={(opts) => approveMut.mutate(opts)}
          pending={approveMut.isPending}
        />
      )}
      {dialog === 'reattest' && (
        <ApproveDialog
          titleKey="audit:dialog.reattest_title"
          onCancel={() => setDialog(null)}
          onConfirm={(opts) => reattestMut.mutate(opts)}
          pending={reattestMut.isPending}
        />
      )}
    </div>
  );
}

interface ReactButtonSpec {
  key: string;
  label: string;
  icon: IconName;
  onClick: () => void;
  pending: boolean;
  variant: 'default' | 'outline';
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

interface RationaleDialogProps {
  titleKey: string;
  onCancel: () => void;
  onConfirm: (rationale: string) => void;
  pending: boolean;
}

function RationaleDialog({ titleKey, onCancel, onConfirm, pending }: RationaleDialogProps): JSX.Element {
  const { t } = useTranslation(['audit', 'common']);
  const [rationale, setRationale] = useState<string>('');
  const [touched, setTouched] = useState<boolean>(false);
  const titleId = useId();

  const trimmed = rationale.trim();
  const valid = trimmed.length >= 1;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    onConfirm(trimmed);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="rationale-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
        <h3 id={titleId} className="mb-4 text-lg font-semibold">
          {t(titleKey)}
        </h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('audit:dialog.rationale_label')}</span>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder={t('audit:dialog.rationale_placeholder')}
              required
              minLength={1}
              rows={4}
              disabled={pending}
              data-testid="rationale-input"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          {touched && !valid && (
            <p role="alert" data-testid="rationale-error" className="text-xs text-destructive">
              {t('audit:dialog.rationale_required')}
            </p>
          )}
          <footer className="mt-2 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={pending}
              data-testid="rationale-cancel"
            >
              {t('audit:dialog.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={pending || !valid}
              data-testid="rationale-confirm"
            >
              {pending ? t('audit:dialog.submitting') : t('audit:dialog.confirm')}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}

interface ApproveDialogProps {
  titleKey: string;
  onCancel: () => void;
  onConfirm: (opts: ApproveOptions) => void;
  pending: boolean;
}

function ApproveDialog({ titleKey, onCancel, onConfirm, pending }: ApproveDialogProps): JSX.Element {
  const { t } = useTranslation(['audit']);
  // Default to 6 months; user can override or mark permanent.
  const [expiresIn, setExpiresIn] = useState<string>(String(SIX_MONTHS_SECONDS));
  const [permanent, setPermanent] = useState<boolean>(false);
  const titleId = useId();

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (permanent) {
      onConfirm({ mark_permanent: true });
      return;
    }
    const n = Number.parseInt(expiresIn, 10);
    const opts: ApproveOptions = {};
    if (Number.isFinite(n) && n > 0) opts.expires_in_seconds = n;
    onConfirm(opts);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="approve-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
        <h3 id={titleId} className="mb-4 text-lg font-semibold">
          {t(titleKey)}
        </h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('audit:dialog.expires_in_label')}</span>
            <Input
              type="number"
              min={1}
              max={63072000}
              value={expiresIn}
              onChange={(e) => setExpiresIn(e.target.value)}
              disabled={pending || permanent}
              data-testid="approve-expires-input"
            />
            <span className="text-xs text-muted-foreground">
              {t('audit:dialog.expires_in_help')}
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={permanent}
              onChange={(e) => setPermanent(e.target.checked)}
              disabled={pending}
              data-testid="approve-permanent-checkbox"
            />
            <span>{t('audit:dialog.mark_permanent_label')}</span>
          </label>
          <footer className="mt-2 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={pending}
              data-testid="approve-cancel"
            >
              {t('audit:dialog.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={pending}
              data-testid="approve-confirm"
            >
              {pending ? t('audit:dialog.submitting') : t('audit:dialog.confirm')}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentation helpers (2026 detail-page pattern)
// ---------------------------------------------------------------------------

// Resource-kind → icon from the existing registry (no new deps).
const KIND_ICONS: Record<string, IconName> = {
  image: 'image',
  video: 'film',
  audio: 'play-circle',
  rss_feed: 'refresh-cw',
  web_page: 'external-link',
};

/** Icon name for a resource kind; unknown → a neutral layers glyph. */
function kindIcon(kind: string): IconName {
  return KIND_ICONS[kind] ?? 'layers';
}

interface SectionTitleProps {
  id?: string;
  icon: IconName;
  children: ReactNode;
}

/** Card heading with a leading icon — the 2026 section-title pattern. */
function SectionTitle({ id, icon, children }: SectionTitleProps): JSX.Element {
  return (
    <CardHeader>
      <CardTitle
        id={id}
        className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
      >
        <Icon name={icon} size={16} aria-hidden />
        {children}
      </CardTitle>
    </CardHeader>
  );
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------


function formatDate(iso: string, lang: Lang): string {
  return formatDateShared(iso, lang as LocaleType);
}

function formatDateTime(iso: string, lang: Lang): string {
  return formatDateShared(iso, lang as LocaleType);
}

function formatBytes(n: number, lang: Lang): string {
  return formatBytesShared(n, lang as LocaleType);
}

export default ResourceDetailPage;
